import TimelineRow from './TimelineRow';
import { Card, Label, Row, Select, Title } from './ui';
import { JOURNEY_NAMES } from '../../Lifecycle/shared/labels';
import { Scroll } from '../../Lifecycle/shared/Scroll';
import SectionState from '../../Lifecycle/shared/SectionState';

import Button from '@/components/Button';
import { Pagination, TimelineItem } from '@/services/lifecycleAPI';

const LOAD_ERROR = 'Could not load the messages.';

type Props = {
  items: TimelineItem[];
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;
  kind: string;
  channel: string;
  journey: string;
  stepLabel: (key: string) => string;
  page: number;
  onKind: (value: string) => void;
  onChannel: (value: string) => void;
  onJourney: (value: string) => void;
  onPage: (page: number) => void;
  onRetry: () => void;
};

const TimelineList = ({
  items,
  pagination,
  loading,
  error,
  kind,
  channel,
  journey,
  stepLabel,
  page,
  onKind,
  onChannel,
  onJourney,
  onPage,
  onRetry,
}: Props) => {
  const totalPages = pagination?.total_pages || 1;

  return (
    <Card aria-labelledby="timelineTitle" data-testid="timelineList">
      <Row
        css={{
          padding: '14px 20px',
          gap: '12px',
          borderBottom: '1px solid #EDEDED',
        }}
      >
        <Title id="timelineTitle">Messages and events</Title>
        <Label
          as="label"
          css={{
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
            marginLeft: 'auto',
          }}
        >
          Kind
          <Select
            data-testid="timelineKindFilter"
            value={kind}
            onChange={(e) => onKind(e.target.value)}
          >
            <option value="">All</option>
            <option value="message">Messages</option>
            <option value="event">Events</option>
            <option value="legacy">Legacy</option>
          </Select>
        </Label>
        <Label
          as="label"
          css={{ display: 'flex', gap: '6px', alignItems: 'center' }}
        >
          Channel
          <Select
            data-testid="timelineChannelFilter"
            value={channel}
            onChange={(e) => onChannel(e.target.value)}
          >
            <option value="">All</option>
            <option value="sms">SMS</option>
            <option value="push">Push</option>
            <option value="email">Email</option>
          </Select>
        </Label>
        <Label
          as="label"
          css={{ display: 'flex', gap: '6px', alignItems: 'center' }}
        >
          Journey
          <Select
            data-testid="timelineJourneyFilter"
            value={journey}
            onChange={(e) => onJourney(e.target.value)}
          >
            <option value="">All</option>
            {Object.entries(JOURNEY_NAMES).map(([key, name]) => (
              <option key={key} value={key}>
                {key === 'TX' ? name : `${key} · ${name}`}
              </option>
            ))}
          </Select>
        </Label>
      </Row>
      {loading && <SectionState mode="loading" title="timeline" />}
      {!loading && error && items.length === 0 && (
        <SectionState
          error={error}
          mode="error"
          title="timeline"
          onRetry={onRetry}
        />
      )}
      {!loading && !error && items.length === 0 && (
        <SectionState mode="empty" title="No lifecycle messages yet" />
      )}
      {!loading && error && items.length > 0 && (
        <SectionState
          error={error === LOAD_ERROR ? error : `${LOAD_ERROR} ${error}`}
          mode="error"
          title="timeline"
          onRetry={onRetry}
        />
      )}
      {!loading && items.length > 0 && (
        // The shared Lifecycle scroller: macOS hides an unstyled scrollbar, and
        // these 1000px rows are wider than the column on a laptop (RT-12).
        // The pager stays below the scroller, in view, as on System events
        // (review RV-11).
        <>
          <Scroll data-testid="timelineScroll">
            {items.map((item) => (
              <TimelineRow
                item={item}
                // sms_logs and firebase_worker_logs number their rows
                // separately, so a legacy id alone can repeat (review C-04).
                key={
                  item.kind === 'legacy'
                    ? `legacy-${item.source}-${item.id}`
                    : `${item.kind}-${item.id}`
                }
                stepLabel={stepLabel}
              />
            ))}
          </Scroll>
          <Row
            css={{
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderTop: '1px solid #EDEDED',
            }}
          >
            <Label>{`${pagination?.total ?? items.length} items`}</Label>
            <Row css={{ gap: '10px' }}>
              <Button
                a11yLabel="Previous page"
                appearance="outline"
                disabled={page <= 1}
                label="Previous"
                size="sm"
                onClick={() => onPage(page - 1)}
              />
              <Label>{`Page ${page} of ${totalPages} · 25 per page`}</Label>
              <Button
                a11yLabel="Next page"
                appearance="outline"
                disabled={page >= totalPages}
                label="Next"
                size="sm"
                onClick={() => onPage(page + 1)}
              />
            </Row>
          </Row>
        </>
      )}
    </Card>
  );
};

export default TimelineList;
